/**
 * @description
 * React Query hook for test results data management in the MediConnect Healthcare System.
 * This hook provides a standardized interface for fetching and filtering test results
 * with support for various test types and result statuses.
 * 
 * Key features:
 * - Comprehensive test results data fetching with caching
 * - Filtering capabilities for different test types and statuses
 * - Support for historical test data and trending
 * 
 * @dependencies
 * - @tanstack/react-query: For data fetching and state management
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
// Import Supabase client and auth hook
import { supabase } from '../lib/supabase-client'; 
import { useAuth } from '../contexts/auth-context';
// Keep existing frontend types
import { 
  TestResult as AppTestResult, 
  TestComponent as AppTestComponent, 
  TestResultStatus as AppTestResultStatus, 
  TestComponentStatus as AppTestComponentStatus,
  TestCategory as AppTestCategory,
  TestResultRecord
} from '../types/test-result-types';

// Define the structure for filtering options using imported types
interface AppTestResultsFilter {
  searchTerm: string;
  category: AppTestCategory | 'all' | null; // Use imported TestCategory
  status: AppTestResultStatus | 'all'; // Use imported TestResultStatus
  dateRange: { start: string | null; end: string | null }; // Using string for dates from UI inputs
  sortBy: 'orderedDate' | 'testName' | 'status'; // Reflect actual AppTestResult properties
  sortDirection: 'asc' | 'desc'; // Add sort direction
}

// Default filter values using the new interface and imported status
const DEFAULT_FILTERS: AppTestResultsFilter = {
  searchTerm: '',
  category: 'all',
  status: 'all', // Use 'all' as default
  dateRange: { start: null, end: null },
  sortBy: 'orderedDate',
  sortDirection: 'desc', // Add default sort direction
};

// Mock historical data can be kept or removed if not used
// const historicalData = { ... };

// Remove mock test results data
// const mockTestResults: AppTestResult[] = [ ... ];

// Query keys for test results data
export const testResultsKeys = {
  all: ['testResults'] as const,
  // Update filters type in query key definition
  filters: (userId: string | undefined, filters: AppTestResultsFilter) => [...testResultsKeys.all, userId, filters] as const,
  detail: (id: string) => [...testResultsKeys.all, 'detail', id] as const,
};

// --- Helper function to map DB Record (snake_case) to AppTestResult (camelCase) ---
const mapTestResultRecordToAppTestResult = (record: TestResultRecord): AppTestResult => {
  
  let components: AppTestComponent[] = [];

  // Handle results field (jsonb)
  if (record.results) {
    try {
      // Ensure results is treated as an object, even if stored as a string
      const resultsData = typeof record.results === 'string'
        ? JSON.parse(record.results)
        : record.results;

      if (Array.isArray(resultsData)) {
        // Case 1: results is an array of component objects
        components = resultsData.map((compData: any): AppTestComponent => ({
          name: compData.component_name ?? 'Unknown Component',
          resultValue: compData.result_value !== undefined && compData.result_value !== null ? String(compData.result_value) : 'N/A',
          units: compData.units ?? '',
          referenceRange: compData.reference_range ?? '',
          status: (compData.status || 'unknown') as AppTestComponentStatus,
          interpretation: compData.interpretation ?? '',
          notes: compData.notes ?? '',
        }));
      } else if (typeof resultsData === 'object' && resultsData !== null) {
        // Case 2: results is a single object with key-value pairs for components
        components = Object.entries(resultsData).map(([key, value]): AppTestComponent => ({
          name: key, // Use the object key as the component name (e.g., 'systolic', 'weight')
          resultValue: value !== undefined && value !== null ? String(value) : 'N/A',
          // Defaulting other fields, add specific logic if needed based on key
          units: '',
          referenceRange: '',
          status: 'unknown', // Assign a default or derive from value
          interpretation: '',
          notes: '',
          // Ensure all required fields of AppTestComponent are present, even if defaulted
        }));
      } else {
        // Log if it's neither array nor object (and not null/undefined)
        console.warn('Unexpected data type in results field:', typeof resultsData, record.id);
      }
    } catch (e) {
      console.error(`Error parsing 'results' JSONB for test result ${record.id}:`, e, record.results);
      // Keep components as empty array in case of error
    }
  }

  // Map main record fields
  const statusString = record.status || 'unknown';
  return {
    id: record.id,
    patientId: record.patient_id,
    testName: record.test_name ?? 'Unknown Test',
    testType: (record.test_type as AppTestCategory | undefined) ?? 'unknown',
    orderedDate: record.ordered_date ?? new Date().toISOString(),
    completedDate: record.completed_date,
    status: statusString as AppTestResultStatus,
    reviewedBy: record.reviewed_by,
    reviewedDate: record.reviewed_date,
    notes: record.notes,
    doctorNotes: record.doctor_notes,
    flagged: record.flagged ?? false,
    components: components, // Assign the processed components array
    createdAt: record.created_at ?? new Date().toISOString(),
    updatedAt: record.updated_at ?? new Date().toISOString(),
  };
};

// --- Fetch function using Supabase ---\n// Ensure this function returns TestResultRecord[] fetched from DB
const fetchTestResults = async (userId: string | undefined): Promise<TestResultRecord[]> => {
  if (!userId) {
    throw new Error('User not authenticated');
  }

  // Fetch raw data (TestResultRecord)
  const { data, error } = await supabase
    .from('test_results')
    .select('*') // Select all columns matching TestResultRecord
    .eq('patient_id', userId)
    .order('ordered_date', { ascending: false }); 

  if (error) {
    console.error('Error fetching test results:', error);
    throw new Error(error.message || 'Failed to fetch test results');
  }

  // Return the raw data, mapping will happen in useQuery's select
  // Ensure the fetched data structure matches TestResultRecord
  return (data || []) as TestResultRecord[]; 
};

// --- Main Hook --- 
/**
 * Custom hook to fetch and manage test results.
 * Uses React Query for caching and data fetching.
 * 
 * @param {AppTestResultsFilter} initialFilters Initial filters for the test results.
 * @returns {UseTestResultsReturn} An object containing test results and related state.
 */
// Update function signature to use the new filter type and DEFAULT_FILTERS
export function useTestResults(initialFilters: AppTestResultsFilter = DEFAULT_FILTERS): {
  allResults: AppTestResult[];
  filteredResults: AppTestResult[];
  selectedResult: AppTestResult | null;
  filters: AppTestResultsFilter; // Use the new filter type
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  updateFilters: (newFilters: Partial<AppTestResultsFilter>) => void; // Use partial of new type
  resetFilters: () => void;
  selectResult: (id: string) => void;
  clearSelectedResult: () => void;
} {
  const { user } = useAuth();
  const userId = user?.id;

  // State for filters - Use the new filter type and DEFAULT_FILTERS
  const [filters, setFilters] = useState<AppTestResultsFilter>(initialFilters);

  // State for selected result ID
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  
  // Fetch all results using React Query
  const { 
    data: allResults = [], // Default to empty array
    isLoading, 
    isError, 
    error, 
    refetch 
  } = useQuery<TestResultRecord[], Error, AppTestResult[]>({ // Fetch TestResultRecord[], select AppTestResult[]
    queryKey: testResultsKeys.filters(userId, filters), // Use filters in query key
    queryFn: () => fetchTestResults(userId),
    // Use the select option to map the fetched data
    select: (fetchedData: TestResultRecord[]) => {
      return fetchedData.map(mapTestResultRecordToAppTestResult);
    },
    enabled: !!userId, // Only run query if userId is available
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection time
  });
  
  // Memoized filtering logic
  const filteredResults = useMemo(() => {
    if (!allResults) return [];

    let results = [...allResults];
    
    // Apply search term filter (case-insensitive)
    if (filters.searchTerm) {
      const lowerSearchTerm = filters.searchTerm.toLowerCase();
      results = results.filter(result => 
        result.testName?.toLowerCase().includes(lowerSearchTerm) ||
        result.components?.some(c => c.name?.toLowerCase().includes(lowerSearchTerm)) ||
        result.notes?.toLowerCase().includes(lowerSearchTerm) ||
        result.doctorNotes?.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Apply category filter
    if (filters.category && filters.category !== 'all') {
      results = results.filter(result => result.testType === filters.category);
    }

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      results = results.filter(result => result.status === filters.status);
    }

    // Apply date range filter
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);
      results = results.filter(result => new Date(result.orderedDate) >= startDate);
    }
    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      // Adjust to include the end date fully
      endDate.setHours(23, 59, 59, 999); 
      results = results.filter(result => new Date(result.orderedDate) <= endDate);
    }

    // Apply sorting
    results.sort((a, b) => {
      let compareA: string | number | Date = '';
      let compareB: string | number | Date = '';
      
      switch (filters.sortBy) {
        case 'orderedDate':
          // Compare as dates
          compareA = new Date(a.orderedDate);
          compareB = new Date(b.orderedDate);
          break;
        case 'testName':
          compareA = a.testName?.toLowerCase() ?? ''; // Add nullish coalescing
          compareB = b.testName?.toLowerCase() ?? ''; // Add nullish coalescing
          break;
        case 'status':
          compareA = a.status.toLowerCase();
          compareB = b.status.toLowerCase();
          break;
      }

      // Comparison logic
      let comparison = 0;
      if (compareA > compareB) {
        comparison = 1;
      } else if (compareA < compareB) {
        comparison = -1;
      }
      // Use filters.sortDirection
      return filters.sortDirection === 'desc' ? comparison * -1 : comparison;
    });

    return results;
  }, [allResults, filters]); // Ensure filters is dependency

  // Memoized selected result
  const selectedResult = useMemo(() => {
    return allResults.find(result => result.id === selectedResultId) || null;
  }, [allResults, selectedResultId]);

  // --- Action Handlers ---

  // Update filters function
  const updateFilters = (newFilters: Partial<AppTestResultsFilter>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters,
    }));
    // Optionally invalidate query if filters don't fully capture changes in queryKey
    // queryClient.invalidateQueries({ queryKey: testResultsKeys.filters(userId, filters) });
  };

  // Reset filters to default
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  // Select a result by ID
  const selectResult = (id: string) => {
    setSelectedResultId(id);
  };
  
  // Clear selected result
  const clearSelectedResult = () => {
    setSelectedResultId(null);
  };
  
  // Log data for debugging (optional)
  useEffect(() => {
    console.log('Filters updated:', filters);
    console.log('Fetched Results:', allResults.length);
    console.log('Filtered Results:', filteredResults.length);
  }, [filters, allResults, filteredResults]);
  
  return {
    allResults,
    filteredResults,
    selectedResult,
    filters,
    isLoading,
    isError,
    error,
    refetch,
    updateFilters,
    resetFilters,
    selectResult,
    clearSelectedResult,
  };
}

// Remove duplicate export
// export default useTestResults;

// Keep individual exports if they are used elsewhere, otherwise remove
/*
export const useAllTestResults = () => { ... };
export const useTestResult = (id: string) => { ... };
*/ 